import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Deserialize } from 'cerialize';
import { Equipement } from '../models/equipement';
import { CarnetEquipement } from '../models/carnetEquipement';
import { objectToFormData } from "object-to-formdata";
import { EquipementMetadata } from "../models/equipementMetadata";
import { BehaviorSubject, Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import {UserService} from './user.service';
import {Logement} from "../models/logement";
import {LogementService} from "./logement.service";
import {EquipementGlobalPageModule} from "../pages/equipement-global/equipement-global-page.module";

@Injectable({
  providedIn: 'root'
})
export class EquipementService {

  logement: Logement = null;

  baseApiUrl: string = environment.api.baseUrl;

  protected _carnetEquipement: BehaviorSubject<CarnetEquipement[]> = new BehaviorSubject(null);
  public readonly carnetEquipement: Observable<CarnetEquipement[]> = this._carnetEquipement.asObservable().pipe(filter(equipements => equipements != null));

  constructor(private http: HttpClient, private userService: UserService, private logementService: LogementService) {
    logementService.logementCourant.subscribe(logement => this.logement = logement);
  }

  load(logementId: number): Promise<CarnetEquipement[]> {
    return this.http.get(`${this.baseApiUrl}equipements/${logementId}`).toPromise().then(data => {
      const equipements = Deserialize(data, CarnetEquipement);
      this._carnetEquipement.next(equipements);

      return equipements;
    });
  }

  findById(logementId: number, id: number): Observable<CarnetEquipement> {
    if (this._carnetEquipement.value == null) {
      this.load(logementId);
    }

    return  this.carnetEquipement.pipe(map(carnetEquipement =>carnetEquipement.find(carnetEquipement => carnetEquipement.equipement.id == id)));
  }

  public findDetailsById(logementId: number, equipementId: number): Promise<CarnetEquipement> {
    return this.http.get(`${this.baseApiUrl}equipement/${logementId}/${equipementId}`).toPromise().then(
      (data) => {
        return Deserialize(data, CarnetEquipement);
      },
      (data) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public findEquipementDetailsById(equipementId: number): Promise<Equipement> {
    return this.http.get(`${this.baseApiUrl}equipement/${equipementId}`).toPromise().then(
      (data) => {
        return Deserialize(data, Equipement);
      },
      (data) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public loadEquipementsIndividuelsDisponibles(logementId: number): Promise<Equipement[]> {
    return this.http.get(`${this.baseApiUrl}equipements/individuels/disponibles/${logementId}`).toPromise().then(
      (data) => {
        return Deserialize(data, Equipement);
      },
      (data) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public setEquipementsIndividuels(logementId: number, equipements: any): Promise<any> {
    if (equipements.equipements.length == 0) {
      equipements.equipements = null;
    }

    return this.http.post(`${this.baseApiUrl}equipements/individuels/${logementId}`, objectToFormData(equipements)).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status == 400) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public removeEquipementIndividuel(logementId: number, equipementId: number): Promise<any> {
    return this.http.post(`${this.baseApiUrl}equipement/individuel/remove/${logementId}`, objectToFormData({id: equipementId, idUser: this.userService.data.id})).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status == 400) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public getMetadata(logementId: number, equipementId: number): Promise<EquipementMetadata> {
    return this.http.get(`${this.baseApiUrl}equipement/metadata/${logementId}/${equipementId}`).toPromise().then(
      (data) => {
        return Deserialize(data, EquipementMetadata);
      },
      (data) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      }
    );
  }

  public saveMetadata(logementId: number, equipementId: number, metadata: any): Promise<any> {
    return this.http.post(`${this.baseApiUrl}equipement/metadata/${logementId}/${equipementId}`, objectToFormData(metadata)).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status == 400) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      }
    );
  }

  public isAuthorizedToEdit(carnetEquipement: CarnetEquipement)
  {
      return this.userService.isLocataire(this.logement)
        ? this.userService.data.id === carnetEquipement.createur?.id
        : true;
  }

  public createIndividualEquipment(logement: Logement, formdata: any): Promise<any>
  {
    return this.http
      .post(`${this.baseApiUrl}equipement/individuel/new/${logement.id}`, formdata).toPromise().then(
        (data) => {
          return data;
        },
        (data) => {
          if (data.status == 400) {
            return data.error;
          }
          throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
        });
  }
}
